import _ from "lodash";
import {
  backdateTimeTableMutation,
  changeUnitPlanTemplateMutation,
  updateSnpPermissionsMutation,
  uploadBulkAttendanceMutation,
  internalToolsGenericMutation,
  unitPlanBulkUploadMutation,
  unitPlanPDFBulkUploadMutation,
  unitPlanPDFAnnotationMutation,
  bulkUploadUnitExcelMutation,
  finalUnitExcelUploadMutation,
  bulkUploadCalendarMutation,
  finalBulkUploadCalendarMutation,
  addToddleBuddyMutation,
  removeToddleBuddyMutation,
  encryptDataMutation,
  decryptDataMutation,
  createWorkrampFoundationMutation,
  updateWorkrampFoundationMutation,
  deleteWorkrampFoundationMutation,
  createWorkrampTrainingMutation,
  updateWorkrampTrainingMutation,
  deleteWorkrampTrainingMutation,
  updateWorkrampFoundationsOrderMutation,
} from "./InternalToolsMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import client from "lib/apolloClient/client";
import { dispatchError } from "Utils";
import {
  getInternalToolGenericDataQuery,
  getDownloadTemplateFileQuery,
  getJobStatusQuery,
  getFoundationsQuery,
  getTrainingsQuery,
} from "./InternalToolsQuery";
import { downloadFromUrl } from "modules/Services";
import { getOrganizationId } from "modules/CommonSelectors";
import { getInternalToolsGenericQuery } from "Tooling/modules/ToolingQueries";

const MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
import {
  getLibraryPortalActivationStatusQueryVariables,
  getLibraryPortalActivationStatusQueryData,
} from "./InternalToolUtils";
import {
  getInternalToolGenericDataFromCache,
  writeInternalToolGenericDataToCache,
} from "./InternalToolsGraphqlHelper";

export const updateBackdateTimeTable = async ({ input }) => {
  try {
    const response = await client.mutate({
      mutation: backdateTimeTableMutation,
      variables: {
        input,
      },
    });
    return response;
  } catch (err) {
    return {};
  }
};

export const changeUnitPlanTemplate = variables => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: changeUnitPlanTemplateMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.planner.changeUnitPlanTemplate.isSuccess",
        {}
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg: "Unit plans ported successfully",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const unitPlanPDFBulkUpload = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: unitPlanPDFBulkUploadMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.planner.unitPlanPDFBulkUpload.isSuccess",
        {}
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg:
            "Unit plans uploaded. You will receive an email once the process is complete.",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const unitPlanPDFAnnotation = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: unitPlanPDFAnnotationMutation,
        variables: {
          input: variables,
        },
      });

      const { isSuccess, response: url } = _.get(
        response,
        "data.planner.unitPlanPDFAnnotation",
        {}
      );

      if (!isSuccess) {
        throw new Error();
      }
      return { url };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const unitPlanBulkUpload = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: unitPlanBulkUploadMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.planner.unitPlanBulkUpload.isSuccess",
        {}
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg:
            "Unit plans uploaded. You will receive an email once the process is complete.",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const updateSnpPermissions = variables => {
  return async dispatch => {
    try {
      const { data } = await client.mutate({
        mutation: updateSnpPermissionsMutation,
        variables,
      });

      await fetchSnpConfigurationsForOrg({
        organizationId: variables.params.organizationId,
      });

      const isSuccess = _.get(
        data,
        "platform.internalToolsGenericMutation.isSuccess",
        false
      );

      if (!isSuccess) throw new Error();

      dispatch(
        setToastMsg({
          msg: "Permissions updated successfully",
          notString: true,
          type: "success",
        })
      );
    } catch (e) {
      dispatchError({ dispatch, e });
      throw e;
    }
  };
};

export const fetchSnpConfigurationsForOrg = async ({ organizationId }) => {
  const queryVariables = {
    apiType: "FETCH_CURRICULUM_PROGRAM_WITH_SNP_PERM_STATUS",
    params: {
      organizationId,
    },
  };

  try {
    await client.query({
      query: getInternalToolGenericDataQuery,
      variables: {
        input: queryVariables,
      },
    });
  } catch (e) {
    console.error(e);
  }
};

export const downloadAttendanceUploadTemplate = ({
  type,
  subType,
  curriculumProgramId,
  academicYearId,
}) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const organizationId = getOrganizationId(state);
      const data = await client.query({
        query: getDownloadTemplateFileQuery,
        variables: {
          organizationId,
          type,
          subType,
          curriculumProgramId,
          academicYearId,
        },
        fetchPolicy: "network-only",
      });
      const downloadData = _.get(
        data,
        "data.node.organizationDataBulkDownload",
        {}
      );
      return downloadData;
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const downloadCalendarUploadTemplate = ({
  organizationId,
  type,
  subType,
}) => {
  return async dispatch => {
    try {
      const data = await client.query({
        query: getInternalToolsGenericQuery,
        variables: {
          apiType: "FETCH_SCHOOL_CALENDAR_BLANK_ROSTER",
          params: {
            organizationId,
            type,
            subType,
          },
        },
        fetchPolicy: "network-only",
      });
      const downloadData = _.get(
        data,
        "data.platform.internalToolsGenericQuery.data",
        {}
      );
      return downloadData;
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const downloadFileAtClient = ({ fileName, downloadUrl }) => {
  return async dispatch => {
    dispatch(
      downloadFromUrl({
        attachment: {
          url: downloadUrl,
          name: fileName,
          mimeType: MIME_TYPE,
        },
        shouldAskForSave: true,
      })
    );
  };
};

export const getJobStatus = ({ jobId }) => {
  return async dispatch => {
    try {
      const response = await client.query({
        query: getJobStatusQuery,
        variables: {
          id: jobId,
        },
        fetchPolicy: "network-only",
      });
      return _.get(response, "data.node.status", {});
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const onTimetableFilesUpload = ({
  serviceDataUrl,
  service,
  curriculumProgramId,
  academicYearId,
}) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: uploadBulkAttendanceMutation,
        variables: {
          input: {
            serviceDataUrl,
            service,
            curriculumProgramId,
            academicYearId,
          },
        },
      });
      return _.get(result, "data.integration.integrationUploadAttendance", {});
    } catch (error) {
      dispatchError({ dispatch, error });
      return { errors: true };
    }
  };
};

export const updateToddleLibraryPermissions = ({
  mutationVariables,
  queryVariables,
}) => {
  return async dispatch => {
    try {
      const { data } = await client.mutate({
        mutation: internalToolsGenericMutation,
        variables: mutationVariables,
      });

      const {
        organizationId,
        curriculumProgramIds: activeCurriculumProgramIds,
      } = queryVariables?.input?.params || {};

      const libraryPortalActivationStatusQueryResult = getInternalToolGenericDataFromCache(
        getLibraryPortalActivationStatusQueryVariables(organizationId)
      );

      const curriculumPrograms = _.get(
        libraryPortalActivationStatusQueryResult,
        "data.curriculumPrograms"
      );

      const updatedCurriculumPrograms = _.map(
        curriculumPrograms,
        curriculumProgram => {
          const isCurriculumPermissionEnabled = _.includes(
            activeCurriculumProgramIds,
            curriculumProgram.id,
            false
          );

          return {
            ...curriculumProgram,
            libraryEnabled: isCurriculumPermissionEnabled,
          };
        }
      );

      writeInternalToolGenericDataToCache({
        variables: getLibraryPortalActivationStatusQueryVariables(
          organizationId
        ),
        data: getLibraryPortalActivationStatusQueryData({
          curriculumPrograms: updatedCurriculumPrograms,
        }),
      });

      const isSuccess = _.get(
        data,
        "platform.internalToolsGenericMutation.isSuccess",
        false
      );

      if (!isSuccess) throw new Error();

      dispatch(
        setToastMsg({
          msg: "Permissions updated successfully",
          notString: true,
          type: "success",
        })
      );
    } catch (e) {
      dispatchError({ dispatch, e });
      throw e;
    }
  };
};

export const updateToddleLibraryPermissionsV2 = ({
  mutationVariables,
  queryVariables,
}) => {
  return async dispatch => {
    try {
      const { data } = await client.mutate({
        mutation: internalToolsGenericMutation,
        variables: mutationVariables,
        refetchQueries: [
          {
            query: getInternalToolGenericDataQuery,
            variables: queryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });

      const isSuccess = _.get(
        data,
        "platform.internalToolsGenericMutation.isSuccess",
        false
      );

      if (!isSuccess) throw new Error();

      dispatch(
        setToastMsg({
          msg: "Permissions updated successfully",
          notString: true,
          type: "success",
        })
      );
    } catch (e) {
      dispatchError({ dispatch, e });
      throw e;
    }
  };
};

export const bulkUploadUnitExcel = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: bulkUploadUnitExcelMutation,
        variables: {
          input: variables,
        },
      });

      const uploadResult = _.get(
        response,
        "data.planner.unitPlanXlsxValidationAction",
        {}
      );

      if (_.isEmpty(uploadResult)) {
        throw new Error();
      }

      return uploadResult;
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const bulkUploadCalendar = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: bulkUploadCalendarMutation,
        variables: {
          input: variables,
        },
      });

      const uploadResult = _.get(
        response,
        "data.platform.schoolCalendarSheetValidationAction",
        {}
      );

      if (_.isEmpty(uploadResult)) {
        throw new Error();
      }

      return uploadResult;
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const finalUnitExcelUpload = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: finalUnitExcelUploadMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.planner.unitPlanXlsxUploadAction.isSuccess",
        false
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg:
            "Unit plans uploaded. You will receive an email once the process is complete.",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );

      return {
        isSuccess,
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const finalBulkCalendarExcelUpload = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: finalBulkUploadCalendarMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.platform.schoolCalendarSheetUploadAction.isSuccess",
        false
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg: "Calendar events uploaded",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );

      return {
        isSuccess,
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const addToddleBuddy = ({ variables }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: addToddleBuddyMutation,
        variables: {
          input: variables,
        },
      });

      const isSuccess = _.get(
        response,
        "data.platform.addToddleBuddy.isSuccess",
        false
      );

      if (!isSuccess) {
        throw new Error();
      }

      dispatch(
        setToastMsg({
          msg: "Toddle buddy added successfully",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );

      return {
        isSuccess,
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const removeToddleBuddy = ({ id }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: removeToddleBuddyMutation,
        variables: {
          id,
        },
      });

      const isSuccess = _.get(
        response,
        "data.platform.removeToddleBuddy.isSuccess",
        false
      );

      if (!isSuccess) {
        const error = _.get(
          response,
          "data.platform.removeToddleBuddy.warning.error",
          ""
        );
        dispatch(
          setToastMsg({
            msg: error,
            locale_params: [
              {
                isPlainText: true,
              },
            ],
            type: "error",
          })
        );
        return;
      }

      dispatch(
        setToastMsg({
          msg: "Toddle buddy removed successfully",
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );

      return {
        isSuccess,
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const encryptData = ({ text }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: encryptDataMutation,
        variables: {
          input: {
            text,
          },
        },
      });

      return {
        encryptedTextValue: _.get(
          response,
          "data.platform.encryptDataRequest.processedText",
          ""
        ),
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const decryptData = ({ text }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: decryptDataMutation,
        variables: {
          input: {
            text,
          },
        },
      });

      return {
        decryptedTextValue: _.get(
          response,
          "data.platform.decryptDataRequest.processedText",
          ""
        ),
      };
    } catch (err) {
      console.error(err);
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
          position: "toast-bottom-left",
        })
      );
    }
  };
};

export const createWorkrampFoundation = (input, academyId) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: createWorkrampFoundationMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getFoundationsQuery,
            variables: {
              filters: {
                academyId,
              },
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.createWorkrampFoundation.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.createWorkrampFoundation.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Foundation added successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const updateWorkrampFoundation = (input, academyId) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: updateWorkrampFoundationMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getFoundationsQuery,
            variables: {
              filters: { academyId },
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.updateWorkrampFoundation.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.updateWorkrampFoundation.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Foundation updated successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const updateWorkrampFoundationsOrder = (input, academyId) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: updateWorkrampFoundationsOrderMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getFoundationsQuery,
            variables: {
              filters: { academyId },
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.updateWorkrampFoundationsOrder.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.updateWorkrampFoundationsOrder.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Foundations order updated successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const deleteWorkrampFoundation = (input, academyId) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: deleteWorkrampFoundationMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getFoundationsQuery,
            variables: {
              filters: {
                academyId,
              },
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.deleteWorkrampFoundation.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.deleteWorkrampFoundation.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Foundation deleted successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const createWorkrampTraining = (input, queryVars) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: createWorkrampTrainingMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getTrainingsQuery,
            variables: {
              filters: queryVars,
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.createWorkrampTraining.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.createWorkrampTraining.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Training added successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const updateWorkrampTraining = (input, queryVars) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: updateWorkrampTrainingMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getTrainingsQuery,
            variables: {
              filters: queryVars,
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.updateWorkrampTraining.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.updateWorkrampTraining.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Training updated successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};

export const deleteWorkrampTraining = (input, queryVars) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: deleteWorkrampTrainingMutation,
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: getTrainingsQuery,
            variables: {
              filters: queryVars,
            },
          },
        ],
      });

      const isSuccess = _.get(
        response,
        "data.platform.deleteWorkrampTraining.isSuccess",
        false
      );

      if (!isSuccess) {
        const warning = _.get(
          response,
          "data.platform.deleteWorkrampTraining.warning",
          ""
        );
        throw new Error(warning);
      }

      dispatch(
        setToastMsg({
          msg: "Training deleted successfully",
          type: "success",
          locale_params: [
            {
              isPlainText: true,
            },
          ],
        })
      );
    } catch (err) {
      dispatch(
        setToastMsg({
          msg: "toastMsgs:something_went_wrong",
          type: "warning",
        })
      );
      throw err;
    }
  };
};
